import {
  type APIChannel,
  ChannelType,
  formatRelative,
  selectActiveThreads,
  snowflakeDate,
  type ThreadList,
} from '@alternativamais/chat-core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { avisarErro } from '../../../componentes/Avisos';
import { Icone } from '../../../componentes/Icone';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import { useNome } from '../../../chat/ganchos';
import { rotaDoCanal } from '../Canal';
import { CaixaPopover, Carregando, diaCurto, Vazio } from '../componentes';

/** Threads do canal (C3): ativas e arquivadas, com busca e "Criar". */
export function ListaThreads({
  canalId,
  aoFechar,
  aoCriar,
}: {
  canalId: string;
  aoFechar: () => void;
  aoCriar?: () => void;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const canal = useEstado((estado) => estado.channels[canalId]);
  const ativas = useDerivado(
    (estado) => selectActiveThreads(estado, canalId),
    (estado) => [
      canalId,
      estado.guildThreadIds[estado.channels[canalId]?.guild_id ?? ''],
      estado.channels,
      estado.joinedThreads,
    ],
  );
  const [aba, setAba] = useState<'ativas' | 'arquivadas'>('ativas');
  const [busca, setBusca] = useState('');
  const [arquivadas, setArquivadas] = useState<ThreadList | null>(null);

  useEffect(() => {
    if (aba !== 'arquivadas' || arquivadas) return;
    let vivo = true;
    Promise.all([
      cliente.api.archivedThreads(canalId, 'public', { limit: 50 }),
      cliente.api
        .archivedThreads(canalId, 'joined', { limit: 50 })
        .catch(() => ({ threads: [], members: [] }) as ThreadList),
    ])
      .then(([publicas, privadas]) => {
        if (!vivo) return;
        const vistas = new Set<string>();
        const threads = [...publicas.threads, ...privadas.threads].filter(
          (thread) => !vistas.has(thread.id) && vistas.add(thread.id),
        );
        threads.sort(
          (a, b) =>
            Date.parse(b.thread_metadata?.archive_timestamp ?? '') -
            Date.parse(a.thread_metadata?.archive_timestamp ?? ''),
        );
        for (const thread of threads)
          cliente.store.getState().upsertChannel(thread);
        setArquivadas({ threads, members: [], has_more: publicas.has_more });
      })
      .catch((erro) => {
        avisarErro(erro);
        if (vivo) setArquivadas({ threads: [], members: [] });
      });
    return () => {
      vivo = false;
    };
  }, [aba, arquivadas, cliente, canalId]);

  const lista = aba === 'ativas' ? ativas : (arquivadas?.threads ?? null);
  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return (lista ?? []).filter(
      (thread) => !termo || (thread.name ?? '').toLowerCase().includes(termo),
    );
  }, [lista, busca]);

  const abrir = (thread: APIChannel) => {
    aoFechar();
    if (canal) navegar(`${rotaDoCanal(canal)}/threads/${thread.id}`);
  };

  return (
    <CaixaPopover
      titulo="Threads"
      icone="thread"
      largura={480}
      altura={600}
      aoFechar={aoFechar}
      acoes={
        <>
          <label className="flex h-7 w-40 items-center gap-1.5 rounded-md bg-rail px-2 text-texto-3">
            <span className="sr-only">Buscar thread</span>
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Buscar thread"
              className="min-w-0 grow border-0 bg-transparent text-sm text-texto outline-none placeholder:text-texto-3"
            />
            <Icone nome="busca" tamanho={16} />
          </label>
          {aoCriar && (
            <button
              type="button"
              onClick={aoCriar}
              className="h-7 rounded-md bg-destaque px-3 text-sm font-semibold text-destaque-texto"
            >
              Criar
            </button>
          )}
        </>
      }
    >
      <div
        role="tablist"
        aria-label="Filtro de threads"
        className="flex gap-1 border-b border-borda px-3 py-2"
      >
        {(
          [
            ['ativas', `Ativas (${ativas.length})`],
            [
              'arquivadas',
              arquivadas
                ? `Arquivadas (${arquivadas.threads.length}${arquivadas.has_more ? '+' : ''})`
                : 'Arquivadas',
            ],
          ] as const
        ).map(([valor, rotulo]) => (
          <button
            key={valor}
            type="button"
            role="tab"
            aria-selected={aba === valor}
            onClick={() => setAba(valor)}
            className={`h-7 rounded-md px-2.5 text-sm font-semibold ${
              aba === valor
                ? 'bg-ativo text-texto'
                : 'text-texto-2 hover:text-texto'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>
      {lista === null ? (
        <Carregando />
      ) : filtradas.length === 0 ? (
        <Vazio
          icone="thread"
          titulo={
            busca
              ? 'Nenhuma thread com esse nome'
              : aba === 'ativas'
                ? 'Nenhuma thread ativa'
                : 'Nenhuma thread arquivada'
          }
          texto={
            aba === 'ativas' && !busca
              ? 'Comece uma pelo menu de uma mensagem ou pelo botão Criar.'
              : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-1.5 p-2">
          {filtradas.map((thread) => (
            <CartaoThread
              key={thread.id}
              thread={thread}
              aoAbrir={() => abrir(thread)}
            />
          ))}
        </div>
      )}
    </CaixaPopover>
  );
}

function CartaoThread({
  thread,
  aoAbrir,
}: {
  thread: APIChannel;
  aoAbrir: () => void;
}) {
  const autorId = thread.owner_id ?? '';
  const autor = useNome(autorId, thread.guild_id);
  const privada = thread.type === ChannelType.PRIVATE_THREAD;
  const arquivada = thread.thread_metadata?.archived;
  const meta = arquivada
    ? `arquivada em ${diaCurto(thread.thread_metadata?.archive_timestamp ?? new Date().toISOString())}`
    : thread.last_message_id
      ? `ativa ${formatRelative(snowflakeDate(thread.last_message_id))}`
      : '';
  return (
    <button
      type="button"
      onClick={aoAbrir}
      className="flex flex-col gap-1 rounded-lg border border-borda bg-chat px-3 py-2.5 text-left hover:border-texto-3"
    >
      <span className="flex min-w-0 items-center gap-2">
        {privada && (
          <Icone
            nome="cadeado"
            tamanho={14}
            className="shrink-0 text-texto-3"
          />
        )}
        <span className="truncate text-[15px] font-semibold">
          {thread.name}
        </span>
        {privada && (
          <span className="shrink-0 rounded bg-ativo px-1.5 py-px text-[11px] font-semibold text-texto-2">
            Privada
          </span>
        )}
      </span>
      <span className="truncate text-[13px] text-texto-3">
        Iniciada por <span className="font-semibold text-texto-2">{autor}</span>
        {thread.message_count !== undefined &&
          ` · ${thread.message_count} mensagens`}
        {meta && ` · ${meta}`}
      </span>
    </button>
  );
}
